"use client";

/**
 * MatchLiveBoard — esports-style live scoreboard для match-page.
 *
 * Источник данных: backend SignalR хаб `match-live` + REST fallback на
 * `/api/brackets/matches/{matchId}/live-data`. Tauri-клиенты (каждый участник)
 * пушат live-data в backend, backend мерджит gold по игрокам и шлёт в SignalR.
 *
 * Дизайн: вдохновлено LCS/LPL broadcast UI — gold bar с разницей, objectives
 * по командам, per-player ряды с champion portrait, KDA, CS, items, ward,
 * runes, summoner spells.
 *
 * Что отображается **только live** (не из EOG):
 *  - KDA, CS, wardScore, level, items, ward, gold (только активный),
 *    summoner spells, runes, isDead/respawnTimer, championStats (только активный)
 *
 * Что НЕ показываем live:
 *  - Damage dealt, vision score detail — нет в LCU Live Client API
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { Card, CardContent } from "@/shared/ui/cards/card";
import { apiUrl } from "@/shared/api/config";
import {
  getChampionImageUrl,
  getItemImageUrl,
  getSummonerSpellImageUrl,
} from "@/shared/lib/champion-image";
// Руны живут в своём модуле вместе со справочником их имён и иконок — там же,
// где getRuneEntry. Второй такой функции в champion-image.ts заводить не стал:
// одинаковое имя в двух модулях расходится молча (см. ai/known-issues.md).
import { getRuneImageUrl } from "@/shared/config/runes-extended";
import { getObjectiveImageUrl } from "@/shared/lib/objective-image";
import { subscribeToMatch } from "@/shared/lib/signalr/match-live-connection";
import { useVisibleInterval } from "@/shared/lib/hooks/use-visible-interval";
import { LIVE_PUSH_FRESH_MS, useRecentPush } from "@/shared/lib/hooks/use-recent-push";

type Kda = { kills?: number; deaths?: number; assists?: number; Kills?: number; Deaths?: number; Assists?: number };
type LivePlayer = {
  summonerName?: string; SummonerName?: string;
  riotIdGameName?: string; RiotIdGameName?: string;
  championName?: string; ChampionName?: string;
  rawChampionName?: string; RawChampionName?: string;
  team?: string; Team?: string;
  position?: string; Position?: string;
  kda?: Kda; Kda?: Kda;
  creepScore?: number; CreepScore?: number;
  wardScore?: number; WardScore?: number;
  championLevel?: number; ChampionLevel?: number;
  currentGold?: number; CurrentGold?: number;
  isDead?: boolean; IsDead?: boolean;
  respawnTimer?: number; RespawnTimer?: number;
  items?: string[]; Items?: string[];
  ward?: string; Ward?: string;
  discordNick?: string; DiscordNick?: string;
  summonerSpell1Raw?: string; SummonerSpell1Raw?: string;
  summonerSpell2Raw?: string; SummonerSpell2Raw?: string;
  summonerSpell1Name?: string; SummonerSpell1Name?: string;
  summonerSpell2Name?: string; SummonerSpell2Name?: string;
  keystoneRuneId?: number; KeystoneRuneId?: number;
  primaryRuneTreeId?: number; PrimaryRuneTreeId?: number;
  secondaryRuneTreeId?: number; SecondaryRuneTreeId?: number;
};
type ActivePlayerChampionStats = {
  currentHealth?: number; maxHealth?: number;
  resourceValue?: number; resourceMax?: number; resourceType?: string;
  attackDamage?: number; abilityPower?: number; armor?: number; magicResist?: number;
  attackSpeed?: number; moveSpeed?: number; abilityHaste?: number; critChance?: number;
  lifeSteal?: number; omnivamp?: number; tenacity?: number;
  physicalLethality?: number; magicPenetrationFlat?: number; magicPenetrationPercent?: number;
  // PascalCase mirror
  CurrentHealth?: number; MaxHealth?: number;
  ResourceValue?: number; ResourceMax?: number; ResourceType?: string;
  AttackDamage?: number; AbilityPower?: number; Armor?: number; MagicResist?: number;
  AttackSpeed?: number; MoveSpeed?: number; AbilityHaste?: number; CritChance?: number;
  LifeSteal?: number; Omnivamp?: number; Tenacity?: number;
  PhysicalLethality?: number; MagicPenetrationFlat?: number; MagicPenetrationPercent?: number;
};
type ActivePlayer = {
  summonerName?: string; SummonerName?: string;
  level?: number; Level?: number;
  currentGold?: number; CurrentGold?: number;
  championStats?: ActivePlayerChampionStats; ChampionStats?: ActivePlayerChampionStats;
};
type LiveDragon = { type?: string; Type?: string };
type LiveObjectives = {
  baronTeam1?: number; baronTeam2?: number; BaronTeam1?: number; BaronTeam2?: number;
  dragonsTeam1?: LiveDragon[]; dragonsTeam2?: LiveDragon[]; DragonsTeam1?: LiveDragon[]; DragonsTeam2?: LiveDragon[];
  heraldTeam1?: number; heraldTeam2?: number; HeraldTeam1?: number; HeraldTeam2?: number;
  grubsTeam1?: number; grubsTeam2?: number; GrubsTeam1?: number; GrubsTeam2?: number;
  towersTeam1?: number; towersTeam2?: number; TowersTeam1?: number; TowersTeam2?: number;
  inhibitorsTeam1?: number; inhibitorsTeam2?: number; InhibitorsTeam1?: number; InhibitorsTeam2?: number;
};
type LiveDataPayload = {
  matchId?: string;
  gameTime?: number; GameTime?: number;
  gameMode?: string; GameMode?: string;
  activeSummoner?: string; ActiveSummoner?: string;
  activePlayer?: ActivePlayer; ActivePlayer?: ActivePlayer;
  players?: LivePlayer[]; Players?: LivePlayer[];
  objectives?: LiveObjectives; Objectives?: LiveObjectives;
  team1Gold?: number; Team1Gold?: number;
  team2Gold?: number; Team2Gold?: number;
};

// Accept both PascalCase (SignalR/Redis serialized) and camelCase (REST)
function pick<T>(obj: Record<string, unknown> | undefined | null, ...keys: string[]): T | undefined {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null) return v as T;
  }
  return undefined;
}

const RAW_CHAMP_PREFIX = "game_character_displayname_";
function championKey(p: LivePlayer): string | undefined {
  const raw = pick<string>(p as never, "rawChampionName", "RawChampionName");
  if (raw && raw.startsWith(RAW_CHAMP_PREFIX)) {
    return raw.slice(RAW_CHAMP_PREFIX.length);
  }
  return pick<string>(p as never, "championName", "ChampionName");
}

function teamOf(p: LivePlayer): "ORDER" | "CHAOS" | "?" {
  const t = pick<string>(p as never, "team", "Team");
  return t === "ORDER" || t === "CHAOS" ? t : "?";
}

const DRAGON_TYPE_RING: Record<string, string> = {
  fire: "ring-orange-500/80",
  infernal: "ring-orange-500/80",
  water: "ring-sky-400/80",
  ocean: "ring-sky-400/80",
  earth: "ring-amber-700/80",
  mountain: "ring-amber-700/80",
  air: "ring-purple-400/80",
  cloud: "ring-purple-400/80",
  hextech: "ring-cyan-400/80",
  chemtech: "ring-emerald-500/80",
  elder: "ring-yellow-300",
};

function fmtGold(g: number | undefined): string {
  if (g === undefined || g === null) return "—";
  if (g >= 1000) return `${(g / 1000).toFixed(1)}K`;
  return Math.round(g).toString();
}

function ObjIcon({ type, size = "h-5 w-5", title }: { type: string; size?: string; title?: string }) {
  const url = getObjectiveImageUrl(type);
  const t = type.toLowerCase();
  const ring = DRAGON_TYPE_RING[t];
  const ringClass = ring ? `rounded-full ring-2 ${ring} ${t === "elder" ? "animate-pulse" : ""}` : "";
  return (
    <span className={`inline-flex ${ringClass}`} title={title ?? type}>
      <img src={url} alt={type} className={`${size} object-contain`} referrerPolicy="no-referrer" />
    </span>
  );
}

function ItemSlot({ itemId }: { itemId: string | undefined }) {
  if (!itemId || itemId === "0") {
    return <div className="h-6 w-6 rounded-sm bg-muted/40 border border-border" />;
  }
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError — фолбэк загрузки картинки, не пользовательское действие
    <img
      src={getItemImageUrl(itemId)}
      alt={itemId}
      className="h-6 w-6 rounded-sm object-cover bg-muted/40 border border-border"
      referrerPolicy="no-referrer"
      onError={(e) => {
        (e.target as HTMLImageElement).style.visibility = "hidden";
      }}
    />
  );
}

/**
 * Извлекает spell-key из LCU rawDescription.
 * "GeneratedTip_SummonerSpell_SummonerFlash_Description" → "SummonerFlash"
 * Если raw невалиден — пытаемся использовать displayName fallback.
 */
function extractSpellKey(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const m = raw.match(/SummonerSpell_(\w+?)_Description/);
  if (m) return m[1];
  return undefined;
}

function SpellIcon({ rawDesc, name }: { rawDesc?: string; name?: string }) {
  const key = extractSpellKey(rawDesc);
  if (!key) {
    return <div className="h-5 w-5 rounded-sm bg-muted/40 border border-border" title={name} />;
  }
  return (
    <img
      src={getSummonerSpellImageUrl(key)}
      alt={key}
      title={name ?? key}
      className="h-5 w-5 rounded-sm object-cover bg-muted/40 border border-border"
      referrerPolicy="no-referrer"
    />
  );
}

function RuneIcon({ runeId, size = "h-5 w-5", title }: { runeId?: number; size?: string; title?: string }) {
  if (!runeId) {
    return <div className={`${size} rounded-full bg-muted/40 border border-border`} title={title} />;
  }
  return (
    <img
      src={getRuneImageUrl(runeId)}
      alt={String(runeId)}
      title={title ?? `Rune ${runeId}`}
      className={`${size} rounded-full object-cover bg-muted/40 border border-border`}
      referrerPolicy="no-referrer"
    />
  );
}

/** cs/min: знаменатель в минутах, минимум 1 мин чтобы не делить на 0/малое. */
function calcCsMin(cs: number, gameTimeSec: number | undefined): string | undefined {
  if (!gameTimeSec || gameTimeSec < 60) return undefined;
  return (cs / (gameTimeSec / 60)).toFixed(1);
}

function PlayerCell({ p, isMe, mirror = false, gameTime }: { p: LivePlayer; isMe: boolean; mirror?: boolean; gameTime?: number }) {
  const name = pick<string>(p as never, "riotIdGameName", "RiotIdGameName")
    || pick<string>(p as never, "summonerName", "SummonerName")?.split("#")[0]
    || "—";
  const champ = championKey(p);
  const champUrl = champ ? getChampionImageUrl(champ) : undefined;
  const level = pick<number>(p as never, "championLevel", "ChampionLevel");
  const isDead = pick<boolean>(p as never, "isDead", "IsDead");
  const respawn = pick<number>(p as never, "respawnTimer", "RespawnTimer");
  const kda = pick<Kda>(p as never, "kda", "Kda") ?? {};
  const k = kda.kills ?? kda.Kills ?? 0;
  const d = kda.deaths ?? kda.Deaths ?? 0;
  const a = kda.assists ?? kda.Assists ?? 0;
  const cs = pick<number>(p as never, "creepScore", "CreepScore") ?? 0;
  const wardScore = pick<number>(p as never, "wardScore", "WardScore") ?? 0;
  const gold = pick<number>(p as never, "currentGold", "CurrentGold");
  const items = pick<string[]>(p as never, "items", "Items") ?? [];
  const ward = pick<string>(p as never, "ward", "Ward");
  const pos = pick<string>(p as never, "position", "Position");
  const spell1Raw = pick<string>(p as never, "summonerSpell1Raw", "SummonerSpell1Raw");
  const spell2Raw = pick<string>(p as never, "summonerSpell2Raw", "SummonerSpell2Raw");
  const spell1Name = pick<string>(p as never, "summonerSpell1Name", "SummonerSpell1Name");
  const spell2Name = pick<string>(p as never, "summonerSpell2Name", "SummonerSpell2Name");
  const keystone = pick<number>(p as never, "keystoneRuneId", "KeystoneRuneId");
  const primaryTree = pick<number>(p as never, "primaryRuneTreeId", "PrimaryRuneTreeId");

  // Portrait + кластер summoner spells справа от него (или слева в mirror).
  const portrait = (
    <div className={`relative flex items-center gap-1 ${mirror ? "flex-row-reverse" : ""}`}>
      <div className={`relative ${isDead ? "opacity-50 grayscale" : ""}`}>
        {champUrl && (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onError — фолбэк загрузки картинки, не пользовательское действие
          <img
            src={champUrl}
            alt={champ}
            className={`h-12 w-12 rounded-full object-cover border-2 ${isMe ? "border-primary" : "border-border"}`}
            referrerPolicy="no-referrer"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              if (!img.src.endsWith("/images/champion-placeholder.png")) {
                img.src = "/images/champion-placeholder.png";
              }
            }}
          />
        )}
        {level !== undefined && (
          <span className="absolute -bottom-1 -right-1 bg-background border border-border rounded-full text-[10px] font-bold px-1 leading-tight">
            {level}
          </span>
        )}
        {isDead && respawn !== undefined && respawn > 0 && (
          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-red-500 bg-black/60 rounded-full">
            {Math.ceil(respawn)}s
          </span>
        )}
      </div>
      {/* Summoner spells column */}
      <div className="flex flex-col gap-0.5">
        <SpellIcon rawDesc={spell1Raw} name={spell1Name} />
        <SpellIcon rawDesc={spell2Raw} name={spell2Name} />
      </div>
      {/* Keystone rune */}
      <div className="flex flex-col gap-0.5">
        <RuneIcon runeId={keystone} title={`Keystone ${keystone ?? "?"}`} />
        {primaryTree && primaryTree !== keystone && (
          <RuneIcon runeId={primaryTree} size="h-4 w-4" title={`Primary tree ${primaryTree}`} />
        )}
      </div>
    </div>
  );

  const stats = (
    <div className={`flex flex-col gap-0.5 ${mirror ? "items-end text-right" : "items-start"}`}>
      <div className="flex items-baseline gap-1.5">
        <span className={`font-semibold text-sm ${isMe ? "text-primary" : "text-foreground"}`}>{name}</span>
        {pos && pos !== "NONE" && (
          <span className="text-[10px] text-muted-foreground uppercase">{pos}</span>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="tabular-nums">
          <span className="text-emerald-500">{k}</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-red-500">{d}</span>
          <span className="text-muted-foreground">/</span>
          <span className="text-sky-400">{a}</span>
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground tabular-nums" title="Creep score">
          ⚔ {cs}
          {calcCsMin(cs, gameTime) !== undefined && (
            <span className="text-muted-foreground/60 text-[10px]"> ({calcCsMin(cs, gameTime)}/m)</span>
          )}
        </span>
        {wardScore > 0 && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground tabular-nums" title="Vision score">👁 {wardScore.toFixed(0)}</span>
          </>
        )}
        {gold !== undefined && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="text-yellow-400 tabular-nums" title="Current gold">💰 {fmtGold(gold)}</span>
          </>
        )}
      </div>
    </div>
  );

  const itemSlots = (
    <div className={`flex flex-wrap gap-0.5 ${mirror ? "justify-end" : "justify-start"}`}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <ItemSlot key={i} itemId={items[i]} />
      ))}
      <ItemSlot itemId={ward} />
    </div>
  );

  return (
    <div className={`flex items-center gap-3 py-2 ${mirror ? "flex-row-reverse" : ""}`}>
      {portrait}
      <div className={`flex flex-col gap-1 flex-1 min-w-0 ${mirror ? "items-end" : "items-start"}`}>
        {stats}
        {itemSlots}
      </div>
    </div>
  );
}

function GoldBar({ t1, t2 }: { t1: number | undefined; t2: number | undefined }) {
  if (t1 === undefined && t2 === undefined) {
    return <div className="h-1 rounded-full bg-muted" />;
  }
  const a = t1 ?? 0;
  const b = t2 ?? 0;
  const total = a + b;
  const pct1 = total > 0 ? (a / total) * 100 : 50;
  const diff = a - b;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs sm:text-sm tabular-nums font-semibold text-sky-400 min-w-[2.5rem] sm:min-w-[3rem] text-right shrink-0">{fmtGold(a)}</span>
      <div className="relative flex-1 h-2 rounded-full bg-red-500/40 overflow-hidden min-w-0">
        <div className="absolute inset-y-0 left-0 bg-sky-500" style={{ width: `${pct1}%` }} />
      </div>
      <span className="text-xs sm:text-sm tabular-nums font-semibold text-red-400 min-w-[2.5rem] sm:min-w-[3rem] shrink-0">{fmtGold(b)}</span>
      {diff !== 0 && (
        <span className={`absolute left-1/2 -translate-x-1/2 -translate-y-6 text-xs font-semibold ${diff > 0 ? "text-sky-300" : "text-red-300"}`}>
          {diff > 0 ? "▶" : "◀"} {fmtGold(Math.abs(diff))}
        </span>
      )}
    </div>
  );
}

function ObjectivesRow({ obj, team }: { obj: LiveObjectives | undefined; team: 1 | 2 }) {
  if (!obj) return null;
  const dragons = (team === 1
    ? pick<LiveDragon[]>(obj as never, "dragonsTeam1", "DragonsTeam1")
    : pick<LiveDragon[]>(obj as never, "dragonsTeam2", "DragonsTeam2")) ?? [];
  const baron = team === 1
    ? pick<number>(obj as never, "baronTeam1", "BaronTeam1") ?? 0
    : pick<number>(obj as never, "baronTeam2", "BaronTeam2") ?? 0;
  const towers = team === 1
    ? pick<number>(obj as never, "towersTeam1", "TowersTeam1") ?? 0
    : pick<number>(obj as never, "towersTeam2", "TowersTeam2") ?? 0;
  const herald = team === 1
    ? pick<number>(obj as never, "heraldTeam1", "HeraldTeam1") ?? 0
    : pick<number>(obj as never, "heraldTeam2", "HeraldTeam2") ?? 0;
  const grubs = team === 1
    ? pick<number>(obj as never, "grubsTeam1", "GrubsTeam1") ?? 0
    : pick<number>(obj as never, "grubsTeam2", "GrubsTeam2") ?? 0;
  const inhibitors = team === 1
    ? pick<number>(obj as never, "inhibitorsTeam1", "InhibitorsTeam1") ?? 0
    : pick<number>(obj as never, "inhibitorsTeam2", "InhibitorsTeam2") ?? 0;

  return (
    <div className={`flex items-center gap-3 text-sm ${team === 2 ? "flex-row-reverse" : ""}`}>
      {dragons.length > 0 && (
        <div className={`flex items-center gap-0.5 ${team === 2 ? "flex-row-reverse" : ""}`}>
          {dragons.map((d, i) => {
            const t = (d.type ?? d.Type ?? "dragon").toLowerCase();
            return <ObjIcon key={i} type={t} size="h-4 w-4" title={t} />;
          })}
        </div>
      )}
      {herald > 0 && (
        <span className="inline-flex items-center gap-1">
          <ObjIcon type="herald" size="h-4 w-4" title="Herald" />
          {herald > 1 && <span className="text-xs tabular-nums">×{herald}</span>}
        </span>
      )}
      {grubs > 0 && (
        <span className="inline-flex items-center gap-1">
          <ObjIcon type="grub" size="h-4 w-4" title="Void Grubs" />
          <span className="text-xs tabular-nums">×{grubs}</span>
        </span>
      )}
      {baron > 0 && (
        <span className="inline-flex items-center gap-1">
          <ObjIcon type="baron" size="h-4 w-4" title="Baron" />
          {baron > 1 && <span className="text-xs tabular-nums">×{baron}</span>}
        </span>
      )}
      {towers > 0 && (
        <span className="inline-flex items-center gap-1 text-xs">
          <ObjIcon type="tower" size="h-4 w-4" title="Towers destroyed" />
          <span className="tabular-nums font-semibold">{towers}</span>
        </span>
      )}
      {inhibitors > 0 && (
        <span className="inline-flex items-center gap-1 text-xs">
          <ObjIcon type="inhibitor" size="h-4 w-4" title="Inhibitors" />
          <span className="tabular-nums font-semibold">{inhibitors}</span>
        </span>
      )}
    </div>
  );
}

function fmtTime(seconds: number | undefined): string {
  if (!seconds || seconds < 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Панель характеристик игрока, который последним отправил live-data в этот матч.
 * Backend получает их в ActivePlayer.championStats. LCU отдаёт championStats
 * ТОЛЬКО для своего активного игрока, поэтому в чужом матче ты увидишь
 * характеристики того участника матча, чей Tauri-клиент последним пушнул POST.
 *
 * Заголовок показывает имя submitter'а; если это ты сам — пишет "Мои".
 */
function ChampionStatsPanel({ ap, myGameName }: { ap: ActivePlayer | undefined; myGameName?: string }) {
  const tUi = useTranslations("Ui");
  if (!ap) return null;
  const cs = pick<ActivePlayerChampionStats>(ap as never, "championStats", "ChampionStats");
  if (!cs) return null;
  const submitterFull = pick<string>(ap as never, "summonerName", "SummonerName") ?? "";
  const submitterName = submitterFull.split("#")[0] ?? submitterFull;
  const isMine = Boolean(myGameName && submitterName && submitterName.toLowerCase() === myGameName.toLowerCase());
  const headerLabel = isMine ? tUi("litMatchLiveBoard.moiHarakteristiki") : tUi("litMatchLiveBoard.harakteristiki", { name: submitterName });
  const hp = pick<number>(cs as never, "currentHealth", "CurrentHealth");
  const hpMax = pick<number>(cs as never, "maxHealth", "MaxHealth");
  const mana = pick<number>(cs as never, "resourceValue", "ResourceValue");
  const manaMax = pick<number>(cs as never, "resourceMax", "ResourceMax");
  const resType = pick<string>(cs as never, "resourceType", "ResourceType");
  const ad = pick<number>(cs as never, "attackDamage", "AttackDamage");
  const ap_ = pick<number>(cs as never, "abilityPower", "AbilityPower");
  const armor = pick<number>(cs as never, "armor", "Armor");
  const mr = pick<number>(cs as never, "magicResist", "MagicResist");
  const ahaste = pick<number>(cs as never, "abilityHaste", "AbilityHaste");
  const crit = pick<number>(cs as never, "critChance", "CritChance");
  const as_ = pick<number>(cs as never, "attackSpeed", "AttackSpeed");
  const ms = pick<number>(cs as never, "moveSpeed", "MoveSpeed");
  const lethality = pick<number>(cs as never, "physicalLethality", "PhysicalLethality");
  const magicPenFlat = pick<number>(cs as never, "magicPenetrationFlat", "MagicPenetrationFlat");

  const hpPct = hp !== undefined && hpMax ? (hp / hpMax) * 100 : 0;
  const manaPct = mana !== undefined && manaMax ? (mana / manaMax) * 100 : 0;
  const resColor = resType === "MANA" ? "bg-sky-500" : resType === "ENERGY" ? "bg-yellow-400" : "bg-purple-500";

  const Stat = ({ label, value, format }: { label: string; value: number | undefined; format?: (v: number) => string }) => (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums font-medium">
        {value === undefined ? "—" : format ? format(value) : Math.round(value)}
      </span>
    </div>
  );

  return (
    <div className="rounded-md border border-border bg-muted/20 p-2 space-y-2">
      <div className="text-xs font-semibold text-muted-foreground uppercase">{headerLabel}</div>
      {/* HP bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-emerald-400">HP</span>
          <span className="tabular-nums">{hp !== undefined ? Math.round(hp) : "—"} / {hpMax !== undefined ? Math.round(hpMax) : "—"}</span>
        </div>
        <div className="h-1.5 rounded-full bg-emerald-900/40 overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${hpPct}%` }} />
        </div>
      </div>
      {/* Mana/resource bar */}
      {manaMax !== undefined && manaMax > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-sky-300">{resType ?? "MP"}</span>
            <span className="tabular-nums">{mana !== undefined ? Math.round(mana) : "—"} / {Math.round(manaMax)}</span>
          </div>
          <div className="h-1.5 rounded-full bg-sky-900/40 overflow-hidden">
            <div className={`h-full ${resColor}`} style={{ width: `${manaPct}%` }} />
          </div>
        </div>
      )}
      {/* Combat stats */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1 border-t border-border">
        <Stat label="AD" value={ad} />
        <Stat label="AP" value={ap_} />
        <Stat label="Armor" value={armor} />
        <Stat label="MR" value={mr} />
        <Stat label="Haste" value={ahaste} />
        <Stat label="Crit" value={crit} format={(v) => `${Math.round(v * 100)}%`} />
        <Stat label="AS" value={as_} format={(v) => v.toFixed(2)} />
        <Stat label="MS" value={ms} />
        {lethality !== undefined && lethality > 0 && <Stat label="Letality" value={lethality} />}
        {magicPenFlat !== undefined && magicPenFlat > 0 && <Stat label="M.Pen" value={magicPenFlat} />}
      </div>
    </div>
  );
}

/**
 * Banner с цифровыми итогами objectives на каждую команду (как в LCS overlay):
 * 4 цифры подряд: drakes / barons / towers / kills.
 */
function ObjectivesBanner({ obj, players, mirror = false }: { obj: LiveObjectives | undefined; players: LivePlayer[]; mirror?: boolean }) {
  if (!obj) return null;
  const team = mirror ? 2 : 1;
  const dragons = (team === 1
    ? pick<LiveDragon[]>(obj as never, "dragonsTeam1", "DragonsTeam1")
    : pick<LiveDragon[]>(obj as never, "dragonsTeam2", "DragonsTeam2")) ?? [];
  const baron = team === 1
    ? pick<number>(obj as never, "baronTeam1", "BaronTeam1") ?? 0
    : pick<number>(obj as never, "baronTeam2", "BaronTeam2") ?? 0;
  const towers = team === 1
    ? pick<number>(obj as never, "towersTeam1", "TowersTeam1") ?? 0
    : pick<number>(obj as never, "towersTeam2", "TowersTeam2") ?? 0;
  // Team total kills = сумма kills всех игроков команды
  const kills = players.reduce((sum, p) => {
    const k = (pick<Kda>(p as never, "kda", "Kda")?.kills ?? pick<Kda>(p as never, "kda", "Kda")?.Kills) ?? 0;
    return sum + k;
  }, 0);

  const cell = (icon: string, value: number, title: string) => (
    <div className="flex flex-col items-center min-w-[40px]">
      <ObjIcon type={icon} size="h-4 w-4" title={title} />
      <span className="text-sm tabular-nums font-semibold">{value}</span>
    </div>
  );

  return (
    <div className={`flex items-center gap-3 ${mirror ? "flex-row-reverse" : ""}`}>
      {cell("dragon", dragons.length, "Drakes")}
      {cell("baron", baron, "Baron")}
      {cell("tower", towers, "Towers")}
      <div className="flex flex-col items-center min-w-[40px]">
        <span className="text-xs">⚔️</span>
        <span className="text-sm tabular-nums font-semibold">{kills}</span>
      </div>
    </div>
  );
}

export interface MatchLiveBoardProps {
  matchId: string;
  team1Name?: string;
  team2Name?: string;
  /** Когда у юзера есть Riot ID — подсветим его строку. */
  myGameName?: string;
  className?: string;
}

export function MatchLiveBoard({ matchId, team1Name, team2Name, myGameName, className }: MatchLiveBoardProps) {
  const tUi = useTranslations("Ui");
  const [payload, setPayload] = useState<LiveDataPayload | null>(null);

  // REST initial + polling fallback (на случай если SignalR не подключился)
  const fetchLiveData = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/brackets/matches/${matchId}/live-data`, { credentials: "include" });
      if (!r.ok) return;
      const data = await r.json();
      setPayload(data);
    } catch {
      // Best-effort: следующий poll повторит попытку, не шумим тостом на каждый пропущенный тик
    }
  }, [matchId]);

  useEffect(() => {
    void fetchLiveData();
  }, [fetchLiveData]);

  // KAN-935: 5-секундный фолбэк работал и в свёрнутой вкладке — доска живой
  // игры обновлялась для никого. Основной источник тут пуш (`MatchLiveData`),
  // этот интервал только страхует от непошедшего SignalR.
  // KAN-983: гейта «пуш не пошёл» не было — опрос шёл и при живом пуше и мог
  // перезаписать свежий пуш более старым ответом. Пока пуш свежий, тик пропускается.
  const recentPush = useRecentPush(LIVE_PUSH_FRESH_MS);
  useVisibleInterval(() => {
    if (!recentPush.isFresh()) void fetchLiveData();
  }, matchId ? 5000 : null);

  // SignalR live push — KAN-43: race-safe подписка (см. subscribeToMatch).
  useEffect(() => {
    if (!matchId) return;
    return subscribeToMatch<LiveDataPayload>(apiUrl, matchId, (p) => {
      if (p.matchId !== matchId) return;
      recentPush.mark();
      setPayload(p);
    });
  }, [matchId, recentPush]);

  const gameTime = pick<number>(payload as never, "gameTime", "GameTime");
  const gameMode = pick<string>(payload as never, "gameMode", "GameMode");
  const players = useMemo(
    () => pick<LivePlayer[]>(payload as never, "players", "Players") ?? [],
    [payload],
  );
  const objectives = pick<LiveObjectives>(payload as never, "objectives", "Objectives");
  const team1Gold = pick<number>(payload as never, "team1Gold", "Team1Gold");
  const team2Gold = pick<number>(payload as never, "team2Gold", "Team2Gold");
  const activePlayer = pick<ActivePlayer>(payload as never, "activePlayer", "ActivePlayer");

  const orderPlayers = useMemo(() => players.filter(p => teamOf(p) === "ORDER"), [players]);
  const chaosPlayers = useMemo(() => players.filter(p => teamOf(p) === "CHAOS"), [players]);

  const noData = !payload || players.length === 0;

  return (
    <Card className={className}>
      <CardContent className="p-2 sm:p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 sm:gap-4 min-w-0">
          <div className="text-sky-400 font-bold text-base sm:text-lg truncate min-w-0 flex-1">{team1Name ?? "Team 1"}</div>
          <div className="text-[10px] sm:text-xs text-muted-foreground tabular-nums shrink-0 text-center">
            {gameMode && <span className="uppercase block sm:inline">{gameMode}</span>}
            {gameTime !== undefined && <span className="sm:ml-2 block sm:inline">{fmtTime(gameTime)}</span>}
          </div>
          <div className="text-red-400 font-bold text-base sm:text-lg truncate text-right min-w-0 flex-1">{team2Name ?? "Team 2"}</div>
        </div>

        {/* Gold bar */}
        <div className="relative">
          <GoldBar t1={team1Gold} t2={team2Gold} />
        </div>

        {/* Objectives banner (LCS-style 4-cell totals per team) */}
        <div className="flex items-center justify-between gap-4 py-1 border-y border-border">
          <ObjectivesBanner obj={objectives} players={orderPlayers} />
          <div className="flex-1" />
          <ObjectivesBanner obj={objectives} players={chaosPlayers} mirror />
        </div>

        {/* Objectives row (detailed icons including dragon types) */}
        {objectives && (
          <div className="flex items-center justify-between gap-4">
            <ObjectivesRow obj={objectives} team={1} />
            <div className="flex-1" />
            <ObjectivesRow obj={objectives} team={2} />
          </div>
        )}

        {noData ? (
          <div className="text-center text-muted-foreground text-sm py-8">
            {tUi("matchLiveBoard.liveDannyeEscheNe")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 border-t border-border pt-3">
            <div className="space-y-1">
              {orderPlayers.map((p, i) => {
                const name = pick<string>(p as never, "riotIdGameName", "RiotIdGameName")
                  || pick<string>(p as never, "summonerName", "SummonerName")?.split("#")[0];
                const isMe = Boolean(myGameName && name && name.toLowerCase() === myGameName.toLowerCase());
                return <PlayerCell key={i} p={p} isMe={isMe} gameTime={gameTime} />;
              })}
            </div>
            <div className="space-y-1">
              {chaosPlayers.map((p, i) => {
                const name = pick<string>(p as never, "riotIdGameName", "RiotIdGameName")
                  || pick<string>(p as never, "summonerName", "SummonerName")?.split("#")[0];
                const isMe = Boolean(myGameName && name && name.toLowerCase() === myGameName.toLowerCase());
                return <PlayerCell key={i} p={p} isMe={isMe} mirror gameTime={gameTime} />;
              })}
            </div>
          </div>
        )}

        {/* Champion stats panel — характеристики submitter'а (того кто последним
            пушнул /live-data). Заголовок «Мои» если это ты, иначе имя submitter'а. */}
        {activePlayer && (
          <ChampionStatsPanel ap={activePlayer} myGameName={myGameName} />
        )}
      </CardContent>
    </Card>
  );
}
